import type { ReactNode } from 'react';
import type { DailyRecommendation } from '../../../lib/dailyApi';
import { useI18n } from '../../../contexts/I18nContext';

// Per-kind icon + accent. Icons are inline SVGs to avoid an icon dependency.
type IconProps = { className?: string };

function ClockIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MicIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UsersIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <circle cx="9" cy="8" r="3" />
      <path
        d="M3 20a6 6 0 0 1 12 0M16 6a3 3 0 0 1 0 6M21 20a6 6 0 0 0-4-5.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HandshakeIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <path
        d="m11 17 2 2 4-4 4 4M3 12l4 4 4-4M3 12l5-5 4 3 4-3 5 5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MailIcon({ className }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path
        d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const KIND_META: Record<
  DailyRecommendation['kind'],
  { Icon: (p: IconProps) => ReactNode; ring: string; iconBg: string; iconText: string }
> = {
  prep: {
    Icon: MicIcon,
    ring: 'border-emerald-100',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
  },
  time: {
    Icon: ClockIcon,
    ring: 'border-amber-100',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
  },
  team: {
    Icon: UsersIcon,
    ring: 'border-slate-200',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-600',
  },
  networking: {
    Icon: HandshakeIcon,
    ring: 'border-purple-100',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
  },
  follow_up: {
    Icon: MailIcon,
    ring: 'border-blue-100',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
  },
};

function SparkleIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M12 2l1.8 4.9L18.7 8 13.8 9.8 12 14.7l-1.8-4.9L5.3 8l4.9-1.1L12 2zM19 13l.9 2.4 2.4.9-2.4.9L19 19.6l-.9-2.4-2.4-.9 2.4-.9L19 13z" />
    </svg>
  );
}

interface Props {
  recommendations: DailyRecommendation[];
}

/**
 * AI Insights / Recommendations derived from the day's calendar (CLE-269).
 * Renders one card per recommendation with a per-kind icon. Hidden entirely
 * when the day has no recommendations.
 */
export function RecommendationsSection({ recommendations }: Props) {
  const { t } = useI18n();
  if (recommendations.length === 0) return null;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 font-semibold text-gray-800">
        <SparkleIcon className="h-4 w-4 text-purple-500" />
        {t('daily.recommendations')}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {recommendations.map((rec) => {
          const meta = KIND_META[rec.kind];
          const { Icon } = meta;
          return (
            <div
              key={rec.id}
              className={`flex items-start gap-3 rounded-lg border ${meta.ring} bg-gray-50 p-3`}
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.iconBg} ${meta.iconText}`}
              >
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{rec.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-gray-600">{rec.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
